/**
 * Schedule Write Service (Supabase-only, RPC-backed)
 *
 * Wraps the `save_schedule_version` and `lock_schedule_version` Postgres
 * functions defined in migration 00052. Both functions run inside a single
 * transaction on the server so partial writes never reach the database.
 *
 * Concurrency is enforced server-side at two levels:
 *   1. Coarse: schedule_versions.last_modified_date must match the client's
 *      expected value (catches "someone saved while you were editing").
 *   2. Fine: each upserted activity's updated_at must match the client's
 *      expected value (allows non-overlapping concurrent edits to merge,
 *      surfaces a list of conflict IDs for overlapping ones).
 *
 * Locking irreversibly flips schedule_versions.locked = true and snapshots
 * a baseline into schedule_baselines (type='Locked').
 */

import { supabase } from '@/lib/supabase'
import type { Activity, Relationship, ResourceAssignment } from '@/types/scheduling'

// ---------------------------------------------------------------------------
// Errors
// ---------------------------------------------------------------------------

export class ScheduleWriteUnsupported extends Error {
  constructor() {
    super('Saving requires Supabase mode (set VITE_DATA_SOURCE=supabase).')
    this.name = 'ScheduleWriteUnsupported'
  }
}

export class ScheduleSaveConflict extends Error {
  serverLastModifiedDate: string
  serverLastModifiedBy: string
  constructor(serverLastModifiedDate: string, serverLastModifiedBy: string) {
    super('This schedule was modified by another user since you opened it.')
    this.name = 'ScheduleSaveConflict'
    this.serverLastModifiedDate = serverLastModifiedDate
    this.serverLastModifiedBy = serverLastModifiedBy
  }
}

/**
 * Per-row conflict: at least one activity we tried to upsert had its
 * updated_at advance since we read it. The user must pick whose changes win.
 */
export class ScheduleRowConflict extends Error {
  conflictActivityIds: string[]
  constructor(conflictActivityIds: string[]) {
    super(
      `${conflictActivityIds.length} activit${conflictActivityIds.length === 1 ? 'y was' : 'ies were'} modified by another user.`,
    )
    this.name = 'ScheduleRowConflict'
    this.conflictActivityIds = conflictActivityIds
  }
}

export class ScheduleVersionLocked extends Error {
  constructor(message: string) {
    super(message)
    this.name = 'ScheduleVersionLocked'
  }
}

/**
 * Raised when archiving the version a project currently uses as its default.
 * Every project keeps exactly one live default, so the caller must promote a
 * different version first.
 */
export class ScheduleDefaultVersionProtected extends Error {
  constructor() {
    super('This is the default version. Set another version as default before archiving it.')
    this.name = 'ScheduleDefaultVersionProtected'
  }
}

// ---------------------------------------------------------------------------
// Public types
// ---------------------------------------------------------------------------

export interface ScheduleSavePayload {
  versionId: string
  /** lastModifiedDate observed at fetch time. */
  expectedLastModifiedDate: string

  activities: Activity[]
  relationships: Relationship[]
  resourceAssignments: ResourceAssignment[]

  /** IDs that were present at fetch time. Used to compute deletes. */
  originalActivityIds: string[]
  originalRelationshipIds: string[]
  originalAssignmentIds: string[]

  /**
   * Per-activity updated_at as observed at fetch time. The RPC checks every
   * existing activity in the upsert payload against this map. Brand-new
   * activities (id not in the map) skip the check.
   */
  expectedActivityUpdatedAt: Record<string, string>

  savedByUserId: string
}

export interface ScheduleSaveSummary {
  inserted: { activities: number; relationships: number; assignments: number }
  updated: { activities: number; relationships: number; assignments: number }
  deleted: { activities: number; relationships: number; assignments: number }
  newLastModifiedDate: string
}

export interface ScheduleLockResult {
  baselineId: string
  lockedAt: string
  activityCount: number
}

// ---------------------------------------------------------------------------
// Mode guard
// ---------------------------------------------------------------------------

function isSupabaseMode(): boolean {
  return (import.meta.env.VITE_DATA_SOURCE as string) === 'supabase'
}

// ---------------------------------------------------------------------------
// Activity → snake_case row (for jsonb_to_recordset on the server)
// ---------------------------------------------------------------------------

function activityRow(a: Activity) {
  return {
    id: a.id,
    activity_id: a.activityId,
    name: a.name,
    description: a.description,
    activity_type: a.activityType,
    duration_type: a.durationType,
    wbs_id: a.wbsId,
    original_duration: a.originalDuration,
    remaining_duration: a.remainingDuration,
    actual_duration: a.actualDuration,
    early_start: a.earlyStart,
    early_finish: a.earlyFinish,
    late_start: a.lateStart,
    late_finish: a.lateFinish,
    actual_start: a.actualStart,
    actual_finish: a.actualFinish,
    baseline_start: a.baselineStart,
    baseline_finish: a.baselineFinish,
    percent_complete: a.percentComplete,
    percent_complete_type: a.percentCompleteType,
    status: a.status,
    total_float: a.totalFloat,
    free_float: a.freeFloat,
    constraint_type: a.constraintType,
    constraint_date: a.constraintDate,
    constraint_classification: a.constraintClassification,
    calendar_id: a.calendarId,
    priority: a.priority,
    notes: a.notes,
    steps: a.steps,
    activity_codes: a.activityCodes,
    is_critical: a.isCritical,
    is_driving: a.isDriving,
    effort_distributed: a.effortDistributed,
    custom_text: a.customText,
    custom_number: a.customNumber,
    custom_date: a.customDate,
    milestone_category: a.milestoneCategory,
    customer_facing_name: a.customerFacingName,
    customer_commitment_date: a.customerCommitmentDate,
    linked_contract_id: a.linkedContractId,
    linked_contract_milestone_label: a.linkedContractMilestoneLabel,
    has_billing_impact: a.hasBillingImpact,
    billing_amount: a.billingAmount,
    billing_currency: a.billingCurrency,
    revenue_recognition_trigger: a.revenueRecognitionTrigger,
    lifecycle_phase_transition: a.lifecyclePhaseTransition,
    classified_by: a.classifiedBy,
    classified_at: a.classifiedAt,
  }
}

function relationshipRow(r: Relationship) {
  return {
    id: r.id,
    predecessor_activity_id: r.predecessorActivityId,
    successor_activity_id: r.successorActivityId,
    type: r.type,
    lag: r.lag,
    driving: r.driving,
  }
}

function assignmentRow(a: ResourceAssignment) {
  return {
    id: a.id,
    activity_id: a.activityId,
    resource_id: a.resourceId,
    planned_units: a.plannedUnits,
    actual_units: a.actualUnits,
    remaining_units: a.remainingUnits,
    planned_cost: a.plannedCost,
    actual_cost: a.actualCost,
    remaining_cost: a.remainingCost,
    at_completion_cost: a.atCompletionCost,
    curve: a.curve,
    rate_category: a.rateCategory,
  }
}

// ---------------------------------------------------------------------------
// Diff helpers
// ---------------------------------------------------------------------------

function deletedIds<T extends { id: string }>(current: T[], originalIds: string[]): string[] {
  const currentIds = new Set(current.map(x => x.id))
  return originalIds.filter(id => !currentIds.has(id))
}

// ---------------------------------------------------------------------------
// Postgres error mapping
// ---------------------------------------------------------------------------

interface PgError {
  code?: string
  message: string
  details?: string
}

function mapSaveError(err: PgError): never {
  const detail = err.details ?? ''
  // Conflict detail formats produced by the RPC:
  //   "conflict|<lastModifiedDate>|<lastModifiedBy>"  -> coarse conflict
  //   "row_conflict|<id1>,<id2>,..."                  -> fine conflict
  //   "locked"                                         -> version locked
  if (detail.startsWith('conflict|')) {
    const [, lastModifiedDate, lastModifiedBy] = detail.split('|')
    throw new ScheduleSaveConflict(
      lastModifiedDate ?? '',
      lastModifiedBy ?? 'another user',
    )
  }
  if (detail.startsWith('row_conflict|')) {
    const ids = (detail.slice('row_conflict|'.length) || '')
      .split(',')
      .map(s => s.trim())
      .filter(Boolean)
    throw new ScheduleRowConflict(ids)
  }
  if (detail === 'locked' || detail === 'already_locked') {
    throw new ScheduleVersionLocked(err.message || 'Schedule version is locked.')
  }
  if (detail === 'default_version') {
    throw new ScheduleDefaultVersionProtected()
  }
  throw new Error(err.message || 'Schedule save failed.')
}

// ---------------------------------------------------------------------------
// Public: saveScheduleVersion
// ---------------------------------------------------------------------------

export async function saveScheduleVersion(
  payload: ScheduleSavePayload,
): Promise<ScheduleSaveSummary> {
  if (!isSupabaseMode()) throw new ScheduleWriteUnsupported()

  // Build per-row expected updated_at. Only include rows whose ID was present
  // at fetch time; brand-new activities have no DB row to compare against.
  const originalActivitySet = new Set(payload.originalActivityIds)
  const expectedUpdatedAt: Record<string, { expectedUpdatedAt: string }> = {}
  for (const a of payload.activities) {
    if (originalActivitySet.has(a.id)) {
      const known = payload.expectedActivityUpdatedAt[a.id]
      if (known) expectedUpdatedAt[a.id] = { expectedUpdatedAt: known }
    }
  }

  const rpcPayload = {
    versionId: payload.versionId,
    expectedLastModifiedDate: payload.expectedLastModifiedDate,
    savedBy: payload.savedByUserId,
    activities: payload.activities.map(activityRow),
    relationships: payload.relationships.map(relationshipRow),
    assignments: payload.resourceAssignments.map(assignmentRow),
    deleteActivityIds: deletedIds(payload.activities, payload.originalActivityIds),
    deleteRelationshipIds: deletedIds(payload.relationships, payload.originalRelationshipIds),
    deleteAssignmentIds: deletedIds(payload.resourceAssignments, payload.originalAssignmentIds),
    expectedActivityUpdatedAt: expectedUpdatedAt,
  }

  const { data, error } = await supabase.rpc('save_schedule_version', { p_payload: rpcPayload })
  if (error) mapSaveError(error as PgError)

  const result = data as {
    newLastModifiedDate: string
    inserted: { activities: number; relationships: number; assignments: number }
    updated: { activities: number; relationships: number; assignments: number }
    deleted: { activities: number; relationships: number; assignments: number }
  }

  // Async SOP sweep on the schedule version — single eval per save rather
  // than per-activity, since the SOPs that care about schedules are
  // typically rollups ("schedule must have a baseline", "version must have
  // a non-zero activity count").
  void import('@/services/sopEngine').then(({ evaluateOnEvent }) =>
    evaluateOnEvent({
      trigger: 'on_save',
      entityType: 'schedule_versions',
      entityId: payload.versionId,
      payload: {
        versionId: payload.versionId,
        activityCount: payload.activities.length,
        relationshipCount: payload.relationships.length,
        assignmentCount: payload.resourceAssignments.length,
        savedBy: payload.savedByUserId,
        newLastModifiedDate: result.newLastModifiedDate,
        inserted: result.inserted,
        updated: result.updated,
        deleted: result.deleted,
      } as Record<string, unknown>,
      actor: payload.savedByUserId,
    }),
  )

  return {
    newLastModifiedDate: result.newLastModifiedDate,
    inserted: result.inserted,
    updated: result.updated,
    deleted: result.deleted,
  }
}

// ---------------------------------------------------------------------------
// Public: lockScheduleVersion
// ---------------------------------------------------------------------------

export async function lockScheduleVersion(
  versionId: string,
  lockedByUserId: string,
  baselineName?: string,
): Promise<ScheduleLockResult> {
  if (!isSupabaseMode()) throw new ScheduleWriteUnsupported()

  const { data, error } = await supabase.rpc('lock_schedule_version', {
    p_version_id: versionId,
    p_locked_by: lockedByUserId,
    p_baseline_name: baselineName ?? null,
  })
  if (error) mapSaveError(error as PgError)
  const r = data as { baselineId: string; lockedAt: string; activityCount: number }
  return r
}

// ---------------------------------------------------------------------------
// Public: version lifecycle — default, archive, dates
//
// All three wrap SECURITY DEFINER RPCs from migration 00464. They touch the
// schedule_versions row only (no activity data), so they bypass the
// change-controlled save_schedule_version path deliberately: which version is
// "the" one, and whether an old version is still on the list, are operational
// decisions rather than baselined structural changes.
// ---------------------------------------------------------------------------

/**
 * Make `versionId` the project's default version. Clears the flag from the
 * previous default in the same transaction.
 */
export async function setDefaultScheduleVersion(
  versionId: string,
  actorId: string,
): Promise<void> {
  if (!isSupabaseMode()) throw new ScheduleWriteUnsupported()
  const { error } = await supabase.rpc('set_default_schedule_version', {
    p_version_id: versionId,
    p_actor: actorId,
  })
  if (error) mapSaveError(error as PgError)
}

/**
 * Soft-archive (or restore) a schedule version. Archived versions are
 * read-only and hidden from the version switcher until "Show archived" is on.
 * Archiving the default version is refused server-side.
 */
export async function setScheduleVersionArchived(
  versionId: string,
  archived: boolean,
  actorId: string,
): Promise<void> {
  if (!isSupabaseMode()) throw new ScheduleWriteUnsupported()
  const { error } = await supabase.rpc('set_schedule_version_archived', {
    p_version_id: versionId,
    p_archived: archived,
    p_actor: actorId,
  })
  if (error) mapSaveError(error as PgError)
}

/**
 * Update the version's planned project start (the CPM forward-pass anchor)
 * and/or its data date. Pass null to leave one unchanged.
 */
export async function setScheduleVersionDates(
  versionId: string,
  dates: { projectStartDate?: string | null; dataDate?: string | null },
  actorId: string,
): Promise<void> {
  if (!isSupabaseMode()) throw new ScheduleWriteUnsupported()
  const { error } = await supabase.rpc('set_schedule_version_dates', {
    p_version_id: versionId,
    p_project_start: dates.projectStartDate ?? null,
    p_data_date: dates.dataDate ?? null,
    p_actor: actorId,
  })
  if (error) mapSaveError(error as PgError)
}

// ---------------------------------------------------------------------------
// Public: setScheduleWbsNodeAgile
//
// Toggle a schedule WBS node's agile-delivery flag (schedule_wbs_nodes.is_agile,
// migration 00444). Marking a work package agile means its earned value is drawn
// from the story-point rollup (engine/agileEvm.ts) rather than manual progress
// collection, and it renders as a cadence-driven roll-up bar on the Gantt.
// Supabase only; a lightweight single-column update, not routed through the
// change-controlled save RPC (the delivery mode is an operational flag, not a
// baselined structural change).
// ---------------------------------------------------------------------------

export async function setScheduleWbsNodeAgile(nodeId: string, isAgile: boolean): Promise<void> {
  if (!isSupabaseMode()) throw new ScheduleWriteUnsupported()
  const { error } = await supabase
    .from('schedule_wbs_nodes')
    .update({ is_agile: isAgile })
    .eq('id', nodeId)
  if (error) throw new Error(`Failed to update work package delivery mode: ${error.message}`)
}
